// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Picking the machine up and putting it where you want it (M5-13, 06 §6).
//
// **06 §6 asks for "adjustable machine height independent of scale" and never said how.** The
// wrist menu it mentions exists, and a menu is still the wrong first answer: a player who can see
// a cabinet in front of them and wants it lower reaches out and moves it. Written for the headset
// first, where a player tried exactly that and nothing happened, and ported here so the browser's
// WebXR session behaves the same way — the two clients share a spec, not a codebase, and a
// gesture that works on one and not the other is the worst kind of difference.
//
// **A grab is a shove with the hand closed.** `bodyNudge` already decides whether a hand is
// against the cabinet's shell — that is the whole of the hard part. The only thing separating a
// nudge from a grab is what the hand is doing while it touches: an open hand shoves the machine,
// a closed one carries it. That is also true of a real cabinet, which is why it needs no
// explaining to a player.
//
// **That paragraph describes the gesture and not the code, and the gap between them shipped on
// both clients** (M8-40). Nothing here ever asked whether the hand was open, because hand tracking
// will not say reliably at the speed a nudge happens — so a fast hand against a corner post
// satisfied every test in `begins` and started a carry while the same contact delivered a shove.
// A Quest measured three of four carries beginning within 152 ms of a nudge on 1 Sep 2026, each
// rewriting the player's stored placement on release. `nudging` is the half of the sentence that
// can be measured, and both paths now ask it.
//
// **The machine moves, the world does not.** A grab changes the `Placement` and nothing else. It
// cannot affect the simulation, because a `Placement` is not a control event and the only things
// the simulation ever sees are control events — so a player rearranging their machine mid-ball
// cannot nudge it by accident, and the leaderboard never learns that the cabinet moved.
//
// **Yaw and scale are deliberately untouched.** 06 §2 gives resizing and rotation to the four
// corner handles of the placement ritual, which is a different gesture with a different
// affordance. A grab that silently rotated the machine because one hand drifted would be very
// hard to undo by hand.

import { nudging } from './bodyNudge'
import { onAControl, type CabinetControls } from './cabinetControls'
import { poseSpeed } from './poseTracking'
import { toCabinetPoint, type MutablePoint, type Placement } from './placement'
import type { HandReport, HandTracker } from './xrInput'

/** Which hand, if either, is carrying the machine. */
export type GrabHand = 'left' | 'right' | undefined

/**
 * How far outside the shell a closed hand still takes hold, metres.
 *
 * Larger than the nudge's 40 mm contact skin, because grabbing is deliberate and missing it is
 * annoying, while nudging is accidental and over-triggering it ruins a ball. A player reaching
 * for an edge aims at the edge and lands within a hand's width.
 */
export const GRAB_SKIN = 0.09

/**
 * How far the machine may be carried from where it was, metres.
 *
 * Two metres. Not a comfort limit — it is a way back: a grab that flings the cabinet across the
 * room because tracking glitched mid-hold leaves the player looking at nothing, with no way to
 * recover except restarting the session. The clamp keeps it findable.
 */
export const REACH_LIMIT = 2.0

/**
 * One hand carrying the cabinet.
 *
 * Deliberately a small state machine rather than a flag: a grab has to remember *where the hand
 * was when it closed*, because the machine follows the hand's movement since then rather than
 * jumping its centre to the hand. Without that anchor, grabbing the front left corner would
 * teleport the cabinet's origin onto the player's fingertip.
 */
export class CabinetGrab {
  /** Which hand has hold, or `undefined`. */
  held: GrabHand = undefined

  /** True on the frame a grab starts, for the haptic and the click. */
  took = false

  /** True on the frame a grab ends. */
  dropped = false

  /**
   * Whether each hand was closed last frame — **starting closed, deliberately.**
   *
   * The edge is "was open, now shut", and the only honest answer for a hand nobody has seen yet
   * is that it might already be shut. Starting these `false` means the very first frame of a hand
   * that appears already closed reads as a rising edge and grabs the machine, which is the exact
   * bug the edge was added to fix, surviving at frame one. Assume closed until seen open: every
   * hand has to be observed open once before it can take hold of anything.
   */
  private leftWasClosed = true
  private rightWasClosed = true
  private handAtGrab = { x: 0, y: 0, z: 0 }
  private placementAtGrab = { x: 0, y: 0, z: 0 }
  private readonly scratch: MutablePoint = { x: 0, y: 0, z: 0 }
  private readonly scratchTip: MutablePoint = { x: 0, y: 0, z: 0 }

  /** Forget everything. The session ended, or the input method changed under us. */
  release(): void {
    this.held = undefined
    this.took = false
    this.dropped = false
    // Closed, for the same reason they start closed. A cancel that reset these to open would let
    // a hand that is *still* shut re-take the machine on the very next frame, which is not what
    // 06 §2.5 means by releasing an in-progress grab safely.
    this.leftWasClosed = true
    this.rightWasClosed = true
  }

  /**
   * One frame. Returns where the machine should now be.
   *
   * Takes the placement rather than owning it, and gives back a new one, so the rig stays the
   * single owner of where the machine is.
   */
  advance(hands: HandTracker, current: Placement, controls: CabinetControls): Placement {
    this.took = false
    this.dropped = false

    // **The hand has to close *on* the machine, not arrive already closed.** Reaching for a
    // cabinet with a hand that is already shut — carrying something, resting on a knee, or simply
    // reported closed because a joint dropped out — is not a grab, and treating it as one is what
    // "it moves the machine just by having hand near machine" describes. So the gesture is the
    // *edge*: open near the machine, then close.
    const leftClosing = hands.left.grab && !this.leftWasClosed
    const rightClosing = hands.right.grab && !this.rightWasClosed
    this.leftWasClosed = hands.left.grab
    this.rightWasClosed = hands.right.grab

    if (this.held === undefined) {
      if (leftClosing && this.begins(hands.left, current, controls)) this.start('left', hands.left, current)
      else if (rightClosing && this.begins(hands.right, current, controls)) this.start('right', hands.right, current)
      if (this.held === undefined) return current
      this.took = true
      return current
    }

    const holding = this.held === 'left' ? hands.left : hands.right
    if (!holding.present || !holding.grab) {
      this.held = undefined
      this.dropped = true
      return current
    }

    // The machine has moved by exactly as much as the hand has, which is what "carrying" means
    // and what makes it feel like an object rather than like a slider.
    const x = this.placementAtGrab.x + (holding.palm.x - this.handAtGrab.x)
    const y = this.placementAtGrab.y + (holding.palm.y - this.handAtGrab.y)
    const z = this.placementAtGrab.z + (holding.palm.z - this.handAtGrab.z)

    return {
      x: clamped(x, this.placementAtGrab.x),
      y: clamped(y, this.placementAtGrab.y),
      z: clamped(z, this.placementAtGrab.z),
      yaw: current.yaw,
      scale: current.scale,
    }
  }

  private start(which: 'left' | 'right', hand: HandReport, current: Placement): void {
    this.held = which
    this.handAtGrab = { x: hand.palm.x, y: hand.palm.y, z: hand.palm.z }
    this.placementAtGrab = { x: current.x, y: current.y, z: current.z }
  }

  /**
   * Whether this hand is closed on the cabinet.
   *
   * The palm rather than the fingertip: a hand wrapped around a rail has its palm on the wood and
   * its fingertip somewhere underneath, and a fingertip test would let go the moment the player
   * curled their fingers — which is the moment they were most obviously holding on.
   */
  private begins(hand: HandReport, current: Placement, controls: CabinetControls): boolean {
    if (!hand.present || !hand.grab) return false
    const at = toCabinetPoint(current, hand.palm.x, hand.palm.y, hand.palm.z, this.scratch)
    const tip = toCabinetPoint(current, hand.tip.x, hand.tip.y, hand.tip.z, this.scratchTip)

    // **A hand on a control is playing, not rearranging.** 06 §2 is explicit that grabbing the
    // plunger or a flipper button ends the placement ritual and starts the game, and the controls
    // are all *on* the shell — so without this the first thing a closed hand does near the plunger
    // is pick the whole machine up instead of pulling it.
    // **One owner**: `onAControl` moved to `cabinetControls` when the shove detector turned out
    // to need the identical answer and not to be asking it. See its comment for the measurement.
    if (
      onAControl(controls, at.x, at.y, at.z) ||
      onAControl(controls, tip.x, tip.y, tip.z)
    ) {
      return false
    }

    // Either the palm or the fingertips — a pinch closes with the fingers, and the palm is a
  // hand's depth further back. On the Quest that was the whole difference between taking
  // hold of a corner and not; see the C# note for the measurements.
  if (
      !onACorner(controls.body, at.x, at.y, at.z) &&
      !onACorner(controls.body, tip.x, tip.y, tip.z)
    ) {
      return false
    }

    // **A hand travelling at nudge speed is nudging** (M8-40). The shove path and this method were
    // two independent yes/no answers about one hand contact, and a headset returned yes to both.
    // `nudging` is now the single answer, so the two cannot disagree.
    //
    // Last, so that a hand nowhere near a corner is refused for the corner: this is the population
    // where the guard is the only thing between the player and a rewritten placement.
    //
    // **The closing edge is spent.** `advance` latches `wasClosed` before this is consulted, so a
    // refusal costs an open-and-close rather than taking hold as soon as the hand slows. That is
    // deliberate — a hand lingering closed on a corner after shoving is the shape of the defect —
    // and a deliberate carry is unaffected, because reaching for a corner and *then* closing has
    // the hand near stationary, well under 0.25 m/s.
    if (nudging(poseSpeed(hand.tip))) return false

    // **A carry is a closed hand, and this file has said so since M7-13 without testing it**
    // (M8-45). `hand.grab` is a *pinch* -- thumb within 25 mm of index -- which a player pressing a
    // flipper button or resting two fingers together satisfies. A Quest 3 measured it on
    // 1 Sep 2026: a hand read closed in five of eighteen heartbeats of ordinary play, and one of
    // the five became a real carry. The pinch is still right for the plunger, so this is a second
    // question rather than a redefinition of the first.
    //
    // **Unmeasured counts as closed** -- the opposite of how the pinch's staleness is handled, and
    // deliberate: a controller states it, a headset measures it, and anything that does neither
    // must keep behaving exactly as before. See `closedMeasured`.
    return !(hand.closedMeasured && !hand.closed)
  }
}

function clamped(now: number, from: number): number {
  const delta = now - from
  if (delta > REACH_LIMIT) return from + REACH_LIMIT
  if (delta < -REACH_LIMIT) return from - REACH_LIMIT
  return now
}

/**
 * How far from a corner post a closed hand still takes hold, metres.
 *
 * **0.13, after the headset showed 0.24 turning the whole front rail into a handle.** The distance
 * is measured in the horizontal plane to the nearest corner post, so a reach of 0.24 m eats 0.24 m
 * *inward along the front face* — and the cabinet is only 0.72 m wide, so two thirds of its
 * half-width qualified. The device reported `took hold at cabinet-local (0.14, 0.92, -0.71)`, which
 * is the middle of the lockdown bar, and a hand on the plunger at x = 0.29 sat 0.09 m from the post
 * and was taken as a corner grab.
 *
 * 0.13 m leaves the middle of the rail alone and is still reachable, because the **fingertip** is
 * tested as well as the palm. Held identical to `CabinetGrab.CornerReach` in the Unity client
 * deliberately: the two clients share a spec, and a gesture that takes hold on one and not the
 * other is the worst kind of difference. This number was measured on the headset, which is the only
 * place either client's hand tracking can be measured at all.
 */
export const CORNER_REACH = 0.13

/**
 * Whether a point is on one of the cabinet's four corners, in cabinet-local metres.
 *
 * **This used to accept the whole box, and that is what "moving my hand around moves the machine"
 * was.** The old rule asked only how far *outside* the shell the hand was, so every point inside
 * the cabinet passed — a volume roughly 0.9 m by 1.4 m by 1.3 m, which with the machine hanging
 * 0.85 m in front of a seated player is exactly where their hands live. The comment admitted it and
 * allowed it anyway, reasoning that hands read a few centimetres inside a surface they rest on.
 * That is true, and it is an argument for a skin, not for the interior.
 *
 * **A corner post, not a face.** The horizontal distance is measured to the nearest vertical
 * corner — the overshoot on each of the two axes, taken together — so a hand just inside the corner
 * still takes hold while a hand through the glass in the middle does not. Vertically it is the
 * whole post, floor to top, because a player picks up a cabinet by whatever height of the corner
 * their hands happen to be at, and there is nothing else at a corner to confuse it with.
 *
 * Faces are deliberately not handles: an open hand on a face already means something — the nudge
 * reads it as a shove — and the two gestures share a surface, so the closed hand needs somewhere of
 * its own to be unambiguous.
 */
export function onACorner(
  shell: CabinetControls['body'],
  x: number,
  y: number,
  z: number,
): boolean {
  if (y < -GRAB_SKIN || y > shell.top + GRAB_SKIN) return false

  const outX = Math.abs(x) - shell.halfWidth
  const outZ = Math.abs(z) - shell.halfLength
  return outX * outX + outZ * outZ <= CORNER_REACH * CORNER_REACH
}
